import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { updateEmployeeDetails } from "../Auth/action";
import { EditableTable } from "../components/EditableTable";
import { Loader } from "../components/customComponents";
import {
  EMPLOYEES_API,
  NOTIFICATION_CONFIG,
  SEND_MAIL_NOTIFICATION,
  MANDATORY_TRAINING,
} from "../constants/urlConstants";
import { mandatoryTrainingsEmail } from "../constants/emailTemplates";
import { CURRENT_ENVIRONMENT } from "../constants/envConstants";
import { utilities } from "../components/utilities";
import { employeeTableHeaders } from "./empMgmtConstants";

const AddEmployee = () => {
  const { employeeList, staticData } = useSelector(
    (state) => state.EmployeeListReducer
  );

  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);

  const columns = React.useMemo(
    () => employeeTableHeaders(staticData, true, true),
    [staticData]
  );

  const newRow = {
    employee_id: "",
    employee_name: "",
    email_id: "",
    competency: "",
    band_type: "",
    geography: "",
    role_name: "",
    skill_set: "",
    sec_skill_set: "",
    joining_date: "",
  };

  const [data, setData] = useState([newRow]);
  const [originalData] = useState(data);
  const [skipPageReset, setSkipPageReset] = useState(false);

  const ccEmails = useRef("");
  const mandatoryTrainings = useRef([]);

  // Handle editable table changes
  const updateMyData = (rowIndex, columnId, value, isDelete) => {
    setSkipPageReset(true);
    if (isDelete) {
      let currData = [...data];
      currData.splice(rowIndex, 1);
      setData([...currData]);
    } else {
      setData((old) =>
        old.map((row, index) =>
          index === rowIndex ? { ...old[rowIndex], [columnId]: value } : row
        )
      );
    }
  };

  useEffect(() => setSkipPageReset(false), [data]);

  // Get notification & training config
  useEffect(() => {
    axios.get(NOTIFICATION_CONFIG).then((config) => {
      if (config.data.errorType === "Error") {
        utilities.showToast(config.data.errorMessage);
      } else {
        ccEmails.current = config.data.find((i) => i.competency === "All")?.email_id.join(",") || "";
      }
    });

    axios
      .get(MANDATORY_TRAINING)
      .then((res) => (mandatoryTrainings.current = res.data))
      .catch(() => utilities.showToast("Failed to load mandatory trainings", true));
  }, []);

  // Validate required fields
  const checkMandatoryFields = (emp) =>
    emp.employee_id &&
    emp.employee_name &&
    emp.email_id &&
    emp.role_name &&
    emp.band_type &&
    emp.geography &&
    emp.competency &&
    emp.joining_date;

  // Prepare email template
  const prepareEmail = (emp, trainings) => {
    const tdHtml = '<td style="border: 1px solid #ddd; padding: 4px;">';
    const urlEnv = CURRENT_ENVIRONMENT === "dev" ? "dev-" : "";
    const URL = `https://${urlEnv}learning.wavicledata.com/#/trainings/completed/32323986`;
    let table_content = "";

    trainings.forEach((training) => {
      if (
        training.competency.includes(emp.competency) &&
        training.geography === emp.geography
      ) {
        const hyperLink = `<a href="${URL}/${training.training_name}">${training.training_name}</a>`;
        table_content += `<tr><td style="border: 1px solid #ddd; padding: 4px;" rowspan="${
          training.subcategory ? training.subcategory.split(", ").length : 1
        }">${hyperLink}</td>`;

        if (training.subcategory) {
          training.subcategory.split(", ").forEach((sub, idx) => {
            if (idx !== 0) table_content += "</tr><tr>";
            table_content += `${tdHtml}${sub}</td>`;
          });
        } else {
          table_content += `${tdHtml}</td>`;
        }

        table_content += "</tr>";
      }
    });

    return table_content
      ? mandatoryTrainingsEmail(emp.employee_name, table_content)
      : "";
  };

  // Notify new employees via email
  const notifyMandatoryTrainings = async (empList, cc) => {
    const requests = empList
      .filter((e) => e.geography.toLowerCase() === "india")
      .map((emp) => {
        const emailContent = prepareEmail(emp, mandatoryTrainings.current);
        if (!emailContent) return null;

        return axios.post(SEND_MAIL_NOTIFICATION, {
          to: emp.email_id,
          cc,
          subject: "Action Required: Mandatory Trainings for New Employees",
          html: emailContent,
        });
      })
      .filter(Boolean);

    await Promise.all(requests);
  };

  // Save employees
  const uploadEmployees = () => {
    setLoading(true);
    let flagValidation = true;

    data.some((emp) => {
      if (!checkMandatoryFields(emp)) {
        flagValidation = false;
        return true;
      }
    });

    if (!flagValidation) {
      utilities.showToast("Mandatory fields need to be updated", true);
      setLoading(false);
      return;
    }

    const currentDate = new Date();
    const insert_date = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;

    const empList = [];
    const newEmployees = data.map((emp) => {
      empList.push({
        employee_name: emp.employee_name,
        email_id: emp.email_id,
        competency: emp.competency,
        geography: emp.geography,
      });

      // Helper to capitalize first letter
      const capitalizeFirst = (str) =>
        str && typeof str === "string"
          ? str.charAt(0).toUpperCase() + str.slice(1)
          : str;

      return {
        ...emp,
        band: capitalizeFirst(emp.band_type),
        role: capitalizeFirst(emp.role_name),
        band_type: capitalizeFirst(
          staticData.find((x) => x.type === "band" && x.id === emp.band_type)
        ?.description || emp.band_type
        ),
        role_name: capitalizeFirst(
          staticData.find((x) => x.type === "role" && x.id === emp.role_name)
        ?.description || emp.role_name
        ),
        insert_date,
      };
    });

    axios
      .post(`${EMPLOYEES_API}/addemployee`, { Employee_List: newEmployees })
      .then((res) => {
        if (res?.data?.errorMessage) {
          utilities.showToast(res.data.errorMessage, true);
        } else {
          dispatch(updateEmployeeDetails([...employeeList, ...newEmployees]));
          notifyMandatoryTrainings(empList, ccEmails.current).finally(() => {
            setLoading(false);
            utilities.showToast("Users Saved Successfully");
          });
          resetData();
        }
      })
      .catch(() => {
        utilities.showToast("Couldn't Add User!", true);
        setLoading(false);
      });
  };

  const addRow = () => setData([...data, newRow]);
  const resetData = () => setData(originalData);

  return (
    <div className="emp-mgmt-wrap">
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <div style={{ display: "inline-block" }}>
            <i className="fa fa-plus-circle fa-2x add-logo" onClick={addRow} />
          </div>
          <EditableTable
            columns={columns}
            data={data}
            updateMyData={updateMyData}
            skipPageReset={skipPageReset}
          />
          <div className="display-flex">
            <span className="orange-btn" onClick={resetData}>
              Reset Data
            </span>
            &nbsp;&nbsp;
            <span className="blue-btn" onClick={uploadEmployees}>
              Save
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEmployee;
